/* eslint-disable @next/next/no-img-element */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Icongraphy,
  IcongraphyVariant,
  SystemIcongraphy,
  Text,
  pickColor,
} from 'remodeling-web-mobile-sdk-ui';
import { BookmarkButton } from '@design-system/mobile';
import { v4 as uuidv4 } from 'uuid';
import { HEADER_HEIGHT, HEADER_Z_INDEX } from '@/styles';
import useBackHandler from '@/hooks/useBackHandler';
import { useWebApp } from '@/providers/WebAppProvider';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import { cartGateway } from '@/di-container/common/cart/gateway/client';
import useAuthenticated from '@/hooks/useAuthenticated';
import TrackingService from '@/services/tracking';
import HamburgerMenu from '../HamburgerMenu';
import useDisplayComponent from '@/hooks/useDisplayComponent';

type LeftIconType = 'CATEGORY' | 'BACK';
type RightIconType =
  | 'SEARCH'
  | 'HOME'
  | 'SHOPPING'
  | 'SHARE'
  | 'SCRAP'
  | 'CLOSE';

type LeftIcon = {
  type: LeftIconType;
  handleClick?: () => void;
  gtmMenuValue?: string;
};
type RightIcon = {
  type: RightIconType;
  active?: boolean;
  handleClick?: () => void;
  gtmTrackingName?: string;
  gtmMenuValue?: string;
};

export interface HeaderProps {
  title?: React.ReactNode;
  left?: LeftIcon[];
  right?: RightIcon[];
  noSticky?: boolean;
  nativeBackCallbackId?: string;
  disableNativeBack?: boolean;
  shouldIgnoreNativeBack?: boolean;
}

/**
 * 공통헤더
 * @author 이현우
 * @param param0
 * @returns
 */
const Header = ({
  left,
  right,
  title,
  noSticky,
  nativeBackCallbackId,
  disableNativeBack = false,
  shouldIgnoreNativeBack = false,
}: HeaderProps) => {
  const router = useRouter();
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
  const { handleBack, handleInAppBack } = useBackHandler();
  const { isApp } = useWebApp();
  const { handleOpenApp } = useDisplayComponent();

  const hasDrawer = useMemo(
    () => left?.some((item) => item.type === 'CATEGORY'),
    [left],
  );

  const handleOpenDrawer = useCallback(() => {
    setIsOpenDrawer(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsOpenDrawer(false);
  }, []);

  const { custNo } = useAuthenticated();
  const { getCount } = cartGateway();
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    if (right && right.find(({ type }) => type === 'SHOPPING') && custNo) {
      getCount.exec({ custNo }).then((apiResponse) => {
        if (apiResponse.code === 200) {
          setCartCount(apiResponse.data?.cartCnt || 0);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custNo]);

  useEffect(() => {
    document.body.style.overflowY = isOpenDrawer ? 'hidden' : '';
    return () => {
      document.body.style.overflowY = '';
    };
  }, [isOpenDrawer]);

  const LeftIconItems = useMemo(() => {
    const result = left?.map(({ type, handleClick, gtmMenuValue }, index) => {
      switch (type) {
        case 'CATEGORY':
          return (
            <IconItem
              key={`left-icon-item-${index}`}
              icon="ic_category_bold_32"
              handleClick={handleOpenDrawer}
              {...TrackingService.tagGTM('header_left_icon_button', {
                'menu-value': gtmMenuValue || 'category',
              })}
            />
          );
        default:
          return (
            <IconItem
              key={`left-icon-item-${index}`}
              icon="ic_back_bold_32"
              handleClick={handleClick || handleInAppBack}
              {...TrackingService.tagGTM('header_left_icon_button', {
                'menu-value': gtmMenuValue || 'back',
              })}
            />
          );
      }
    });
    return result;
  }, [handleInAppBack, handleOpenDrawer, left]);

  const RightIconItems = useMemo(() => {
    const filterRight = right?.filter?.((item) => {
      return !(isApp() && item.type === 'HOME');
    });
    const result = filterRight?.map(
      ({ type, active, handleClick, gtmTrackingName, gtmMenuValue }, index) => {
        switch (type) {
          case 'SEARCH':
            return (
              <IconItem
                key={`right-icon-item-${index}`}
                icon="ic_search_bold_32"
                handleClick={
                  handleClick ||
                  (() => {
                    if (isApp()) {
                      handleOpenApp({ target: 'SEARCH', channel: 'STORE' });
                    } else {
                      router.push(
                        `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/msearch/searchMain.do?searchTab=goods`,
                      );
                    }
                  })
                }
                {...TrackingService.tagGTM(
                  gtmTrackingName || 'header_right_icon_button',
                  {
                    'menu-value': gtmMenuValue || 'search',
                  },
                )}
              />
            );
          case 'HOME':
            return (
              <IconItem
                key={`right-icon-item-${index}`}
                icon="ic_home_bold_32"
                handleClick={
                  handleClick ||
                  (() => {
                    if (isApp()) {
                      handleOpenApp({ target: 'GNB', channel: 'MAIN' });
                    } else {
                      router.push(
                        `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/main.html`,
                      );
                    }
                  })
                }
                {...TrackingService.tagGTM(
                  gtmTrackingName || 'header_right_icon_button',
                  {
                    'menu-value': gtmMenuValue || 'home',
                  },
                )}
              />
            );
          case 'SHOPPING':
            return (
              <Box
                key={`right-icon-item-${index}`}
                position="relative"
                onClick={
                  handleClick ||
                  (() => {
                    if (isApp()) {
                      handleOpenApp({ target: 'CART', channel: 'STORE' });
                    } else {
                      router.push(
                        `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/morder/goCart.do`,
                      );
                    }
                  })
                }
              >
                <IconItem
                  icon="ic_shopping_bold_32"
                  {...TrackingService.tagGTM(
                    gtmTrackingName || 'header_right_icon_button',
                    {
                      'menu-value': gtmMenuValue || 'shopping',
                    },
                  )}
                />
                {cartCount > 0 && (
                  <Box
                    position="absolute"
                    top={20}
                    right={0}
                    width={26}
                    display="flex"
                    justifyContent="space-around"
                  >
                    <Badge
                      variant="etc02"
                      color={'color_chip.white'}
                      textAlign="center"
                    >
                      {cartCount > 999 ? 999 : cartCount}
                    </Badge>
                  </Box>
                )}
              </Box>
            );
          case 'SHARE':
            return (
              <IconItem
                key={`right-icon-item-${index}`}
                icon="ic_share_bold_32"
                handleClick={
                  handleClick || (() => console.log('공유하기 노출'))
                }
                {...TrackingService.tagGTM(
                  gtmTrackingName || 'header_right_icon_button',
                  {
                    'menu-value': gtmMenuValue || 'share',
                  },
                )}
              />
            );
          case 'SCRAP':
            return (
              <BookmarkButton
                isChecked={active}
                size="lg"
                variant="whiteBG"
                onClick={handleClick || (() => console.log('스크랩 기능 연동'))}
                {...TrackingService.tagGTM(
                  gtmTrackingName || 'header_right_icon_button',
                  {
                    'menu-value': gtmMenuValue || 'scrap',
                  },
                )}
              />
            );
          default:
            return (
              <IconItem
                key={`right-icon-item-${index}`}
                icon="ic_close_bold_32"
                handleClick={
                  handleClick || (() => console.log('닫기버튼 클릭'))
                }
                {...TrackingService.tagGTM(
                  gtmTrackingName || 'header_right_icon_button',
                  {
                    'menu-value': gtmMenuValue || 'close',
                  },
                )}
              />
            );
        }
      },
    );
    return result;
  }, [cartCount, handleOpenApp, isApp, right, router]);

  const { handleFireNativeBack, detachCallback } = useWebApp();
  const [fireNativeBackCallbackId] = useState(
    nativeBackCallbackId || `FireNativeBack-Header-${uuidv4()}`,
  );
  useEffect(() => {
    if (!disableNativeBack) {
      if (right) {
        right.forEach((icon) => {
          if (icon.type === 'CLOSE') {
            handleFireNativeBack(
              `${fireNativeBackCallbackId}-Close`,
              async () => {
                if (!shouldIgnoreNativeBack) {
                  icon.handleClick?.();
                }
              },
            );
          }
        });
      }
      if (left) {
        left.forEach((icon) => {
          if (icon.type === 'BACK') {
            handleFireNativeBack(
              `${fireNativeBackCallbackId}-Back`,
              async () => {
                if (!shouldIgnoreNativeBack) {
                  if (icon.handleClick) {
                    icon.handleClick();
                  } else {
                    handleInAppBack();
                  }
                }
              },
            );
          }
        });
      }
    }
    return () => {
      detachCallback(`${fireNativeBackCallbackId}-Back`);
      detachCallback(`${fireNativeBackCallbackId}-Close`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleBack,
    detachCallback,
    handleFireNativeBack,
    fireNativeBackCallbackId,
    disableNativeBack,
    shouldIgnoreNativeBack,
  ]);

  return (
    <>
      <HeaderWrapper {...TrackingService.tagGTM('header')}>
        <HeaderContainer noSticky={noSticky}>
          <LeftSection>{LeftIconItems}</LeftSection>
          <TitleSection>
            {title || (
              <IconItem
                icon="LOGO"
                handleClick={() => alert('메인페이지이동')}
              />
            )}
          </TitleSection>
          <RightSection>{RightIconItems}</RightSection>
        </HeaderContainer>
      </HeaderWrapper>

      {hasDrawer && (
        <HamburgerMenu
          isOpen={isOpenDrawer}
          handleClose={handleCloseDrawer}
          isLayer
          isHeader
        />
      )}
    </>
  );
};

export default Header;

const IconItem = ({
  handleClick,
  icon,
  ...props
}: {
  handleClick?: () => void;
  icon?: IcongraphyVariant | 'LOGO';
}) => {
  return (
    <IconItemAnchor href={undefined} onClick={handleClick} {...props}>
      {icon === 'LOGO' ? (
        <SystemIcongraphy
          variant="hanssem_logo"
          width={49}
          height={28}
          resize
        />
      ) : (
        <Icongraphy variant={icon as IcongraphyVariant} />
      )}
    </IconItemAnchor>
  );
};

const IconItemAnchor = styled.a`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  width: 40px;
`;

const HeaderWrapper = styled.div`
  height: ${HEADER_HEIGHT}px;
  width: 100%;
`;

const HeaderContainer = styled.header<{ noSticky?: boolean }>`
  ${({ theme, noSticky }) => css`
    position: ${noSticky ? 'relative' : 'fixed'};
    top: 0;
    left: 0;
    right: 0;
    height: ${HEADER_HEIGHT}px;
    width: 100%;
    background: ${theme.colors.natural_color.white};
    z-index: ${HEADER_Z_INDEX};
  `}
`;

const BaseSection = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

const LeftSection = styled(BaseSection)`
  display: flex;
  left: 8px;
`;

const RightSection = styled(BaseSection)`
  display: flex;
  right: 4px;
`;

const TitleSection = styled(BaseSection)`
  ${({ theme }) => css`
    left: 50%;
    transform: translate(-50%, -50%);
    ${theme.typography.subTitle1}

    text-align: center;
    color: ${theme.colors.accent_color.primary.default};
  `}
`;

const Badge = styled(Text)`
  border-radius: 7px;
  min-width: 14px !important;
  padding: 0.5px 3.5px 1.5px;
  background-color: ${(props) =>
    pickColor('accent_color.secondary.red', props.theme.colors)};
`;
